import type { FC } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import type { SignalsChannel } from '../type/SignalsTypes';
import { channelColor, channelLabel, makeTimeTickFormatter } from '../lib/signalChannels';

interface SignalChartStackProps {
  channels: SignalsChannel[];
  startTime: string;
  rowHeight?: number;
}

const DEFAULT_ROW_HEIGHT = 130;

function buildChartData(points: Array<[number, number | null]>) {
  return points.map(([t, value]) => ({ t, value }));
}

// Gap markers (null value) aren't real samples and must not skew the peak readout.
function peak(points: Array<[number, number | null]>): string {
  const values = points.map(([, value]) => value).filter((value): value is number => value !== null);
  if (values.length === 0) return '0.00';
  return Math.max(...values).toFixed(2);
}

interface ChannelChartProps {
  tag: string;
  points: Array<[number, number | null]>;
  color: string;
  showXAxis: boolean;
  label: string;
  peakValue: string;
  rowHeight: number;
  formatTick: (relativeSeconds: number) => string;
}

const ChannelChart: FC<ChannelChartProps> = ({
  tag,
  points,
  color,
  showXAxis,
  label,
  peakValue,
  rowHeight,
  formatTick,
}) => {
  const { t } = useTranslation();
  const gradientId = `gradient-${tag}`;
  const data = buildChartData(points);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ background: color }}
          aria-hidden
        />
        <span className="text-xs font-semibold text-foreground">{label}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {t('signals.chart.peak', { value: peakValue })}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={rowHeight}>
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.25} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
          {showXAxis ? (
            <XAxis
              type="number"
              dataKey="t"
              domain={[0, 'dataMax']}
              tickFormatter={formatTick}
              tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
              label={{
                value: t('signals.chart.timeAxisLabel'),
                position: 'insideBottom',
                offset: -2,
                fontSize: 10,
                fill: 'var(--muted-foreground)',
              }}
            />
          ) : (
            <XAxis type="number" dataKey="t" domain={[0, 'dataMax']} tick={false} axisLine={false} />
          )}
          <YAxis
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            width={35}
          />
          <Tooltip
            labelFormatter={(label) => formatTick(label as number)}
            contentStyle={{
              background: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SignalChartStack: FC<SignalChartStackProps> = ({
  channels,
  startTime,
  rowHeight = DEFAULT_ROW_HEIGHT,
}) => {
  const { t } = useTranslation();
  const formatTick = makeTimeTickFormatter(startTime);

  return (
    <div className="flex flex-col gap-3" data-testid="chart-stack">
      {channels.map((channel, idx) => (
        <ChannelChart
          key={channel.tag}
          tag={channel.tag}
          points={channel.points}
          color={channelColor(idx)}
          showXAxis={idx === channels.length - 1}
          label={channelLabel(channel.tag, t)}
          peakValue={peak(channel.points)}
          rowHeight={rowHeight}
          formatTick={formatTick}
        />
      ))}
    </div>
  );
};
